import React, { useMemo } from 'react';
import BaseWidget from './BaseWidget';
import { resolveKpiMetric } from '../../lib/dashboardMetrics';

/**
 * KPICardWidget Component
 *
 * Displays a key performance indicator with value, trend, and comparison
 */
const KPICardWidget = ({ id, config = {}, onRemove, onConfigure, isEditing, theme = 'light', metrics }) => {
  const {
    metric,
    label: configuredLabel,
    value: configuredValue,
    format: configuredFormat,
    showTrend = true,
    trend: configuredTrend,
    trendDirection: configuredTrendDirection,
    comparisonLabel = 'vs last period',
    icon = '📊',
    color = 'blue'
  } = config;

  // BL-194 Gate 1: read the live org aggregate for the configured metric. A
  // hand-set config value still wins (that is how a custom/formula tile works),
  // and the tile only falls back to zero when there is genuinely no data yet —
  // never to invented numbers.
  const resolved = useMemo(() => {
    if (!metrics?.rollup || configuredValue !== undefined) return null;
    return resolveKpiMetric(metrics.rollup, metric || 'revenue');
  }, [metrics, metric, configuredValue]);

  const label = configuredLabel || resolved?.label || 'KPI Metric';
  const value = configuredValue !== undefined ? configuredValue : (resolved?.value ?? 0);
  const format = configuredFormat || resolved?.format || 'number';
  const valueUnavailable = configuredValue === undefined
    && (!metrics?.rollup || resolved?.available === false);
  const trend = configuredTrend !== undefined ? configuredTrend : (resolved?.hasTrend ? resolved.trend : 0);
  const trendDirection = configuredTrendDirection || resolved?.trendDirection || 'up';

  // Format value based on format type
  const formattedValue = useMemo(() => {
    if (valueUnavailable) return '—';
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value);

      case 'percentage':
        return `${Number(value).toFixed(1)}%`;

      case 'number':
      default:
        return new Intl.NumberFormat('en-US').format(value);
    }
  }, [value, format, valueUnavailable]);

  // Determine trend color
  const trendColor = useMemo(() => {
    if (trend === 0) return 'text-[var(--ink-muted)]';
    if (trend > 0 && trendDirection === 'up') return 'text-[var(--ui-positive)]';
    if (trend < 0 && trendDirection === 'down') return 'text-[var(--ui-positive)]';
    if (trend > 0 && trendDirection === 'down') return 'text-[var(--ui-alert)]';
    if (trend < 0 && trendDirection === 'up') return 'text-[var(--ui-alert)]';
    return 'text-[var(--ink-muted)]';
  }, [trend, trendDirection]);

  // Color variants
  const colorClasses = {
    blue: 'bg-[var(--nav-active-bg)] text-[var(--nav-active-ink)]',
    green: 'bg-[var(--ui-positive)]/10 text-[var(--ui-positive)]',
    purple: 'bg-[var(--rule-magenta)]/10 text-[var(--rule-magenta)]',
    orange: 'bg-[var(--ui-moderate)]/10 text-[var(--ui-moderate)]',
    red: 'bg-[var(--ui-alert)]/10 text-[var(--ui-alert)]'
  };

  return (
    <BaseWidget
      id={id}
      title={label}
      onRemove={onRemove}
      onConfigure={onConfigure}
      isRemovable={isEditing}
      isConfigurable={isEditing}
      theme={theme}
      headerActions={
        isEditing ? (
          <div className="widget-drag-handle px-2 py-1 cursor-move">
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </div>
        ) : null
      }
    >
      <div className="flex flex-col items-center justify-center h-full py-4">
        {/* Icon */}
        <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${colorClasses[color] || colorClasses.blue}`}>
          <span className="text-2xl">{icon}</span>
        </div>

        {/* Value */}
        <div className="text-center mb-2">
          <div className="text-3xl font-bold text-[var(--ink)]" aria-label={valueUnavailable ? 'Metric unavailable' : undefined}>
            {formattedValue}
          </div>
        </div>

        {/* Trend */}
        {showTrend && trend !== 0 && (
          <div className="flex items-center space-x-1">
            {trend > 0 ? (
              <svg className={`w-4 h-4 ${trendColor}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className={`w-4 h-4 ${trendColor}`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
            <span className={`text-sm font-medium ${trendColor}`}>
              {Math.abs(trend).toFixed(1)}%
            </span>
            <span className="text-xs text-[var(--ink-muted)]">
              {comparisonLabel}
            </span>
          </div>
        )}
      </div>
    </BaseWidget>
  );
};

export default KPICardWidget;
